import { Wand2, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/flowly/infra/Button/Button";
import { useAnonymousGate } from "@/hooks/useAnonymousGate";

interface EnhancePromptButtonProps {
  onEnhance: () => void;
  isEnhancing: boolean;
  disabled: boolean;
}

export function EnhancePromptButton({
  onEnhance,
  isEnhancing,
  disabled,
}: EnhancePromptButtonProps) {
  const { t } = useLanguage();

  // Built-in auth gate: improve-prompt hits our LLM endpoint, so it costs us
  // money. Anon click → canonical AuthModal; signed-in click runs as normal.
  const gatedEnhance = useAnonymousGate(onEnhance);

  return (
    <Button
      variant="ghost"
      size="xs"
      rounded="full"
      onClick={gatedEnhance}
      disabled={disabled || isEnhancing}
      className={cn(
        "flex items-center gap-2 px-3 py-1.5 text-xs font-medium transition-all",
        isEnhancing
          ? "bg-brand text-white cursor-wait"
          : !disabled
            ? "bg-brand/20 border border-brand/50 text-brand hover:bg-brand hover:text-white hover:shadow-[0_0_16px_hsl(var(--brand)/0.3)] hover:scale-105 active:scale-95"
            : "bg-secondary border border-border text-muted-foreground cursor-not-allowed opacity-50",
      )}
    >
      {isEnhancing ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
      ) : (
        <Wand2 className="w-3.5 h-3.5" />
      )}
      {isEnhancing ? t.generate.improving : t.generate.enhancePrompt}
    </Button>
  );
}
